import { PRODUCT_MEDIA_3D_VIEWER_LOAD_FAILED_ERROR_REASON } from './product-media-3d-launch-analytics.js';
import {
	PRODUCT_MEDIA_3D_METRIC,
	PRODUCT_MEDIA_3D_VIEWER_ERROR_TYPE,
	PRODUCT_MEDIA_3D_VIEWER_OUTCOME,
} from './product-media-3d-metric.js';

export const PRODUCT_MEDIA_3D_VIEWER_FAILURE_LOG_MESSAGE = '[ProductMedia3D] viewer failure';

export const PRODUCT_MEDIA_3D_VIEWER_LOADED_LOG_MESSAGE = '[ProductMedia3D] viewer loaded';

/**
 * The model rendered and then stopped, most often on WebGL context loss. Observability only —
 * the shopper keeps whatever the viewer still shows, and GA4 load events stay about loading.
 */
export const PRODUCT_MEDIA_3D_VIEWER_RENDER_ERROR_REASON = 'render_error';

/**
 * Bounds `error_reason`. A caller reporting an exception message can carry the signed GLB URL it
 * failed on, and `model_url_host` exists to keep exactly that out of the log.
 */
const SAFE_ERROR_REASON_PATTERN = /^[a-z0-9_]{1,40}$/;

const GLB_LOAD_FAILED_CLASSIFICATION = {
	errorType: PRODUCT_MEDIA_3D_VIEWER_ERROR_TYPE.glbLoadFailed,
	metric: PRODUCT_MEDIA_3D_METRIC.viewerLoadFailed,
	outcome: PRODUCT_MEDIA_3D_VIEWER_OUTCOME.loadFailed,
};

const GLB_RENDER_FAILED_CLASSIFICATION = {
	errorType: PRODUCT_MEDIA_3D_VIEWER_ERROR_TYPE.glbRenderFailed,
	metric: PRODUCT_MEDIA_3D_METRIC.viewerRenderFailed,
	outcome: PRODUCT_MEDIA_3D_VIEWER_OUTCOME.renderFailed,
};

/**
 * @param {unknown} errorReason
 * @returns {string}
 */
function readSafeErrorReason(errorReason) {
	return typeof errorReason === 'string' && SAFE_ERROR_REASON_PATTERN.test(errorReason)
		? errorReason
		: PRODUCT_MEDIA_3D_VIEWER_LOAD_FAILED_ERROR_REASON;
}

/**
 * Splits a render failure from every other failure.
 *
 * error_type and outcome are tags on the log-based metrics, so each stays on the set mirrored in
 * `ProductMedia3dViewerOutcome`. `<model-viewer>` reports one error type for a failed fetch, a 403
 * and an unparseable GLB alike, so those cannot be told apart here. The reason a caller passed
 * still reaches Datadog on `error_reason`, a log attribute that carries no tag cost.
 *
 * @param {unknown} errorReason
 * @returns {{ errorType: string, metric: string, outcome: string }}
 */
function classifyProductMedia3dViewerFailure(errorReason) {
	return errorReason === PRODUCT_MEDIA_3D_VIEWER_RENDER_ERROR_REASON
		? GLB_RENDER_FAILED_CLASSIFICATION
		: GLB_LOAD_FAILED_CLASSIFICATION;
}

/**
 * Host only. A signed or tokenized GLB URL would put one log attribute value per shopper into Datadog.
 *
 * @param {unknown} modelUrl
 * @returns {string}
 */
function readModelUrlHost(modelUrl) {
	if (typeof modelUrl !== 'string' || modelUrl === '') {
		return '';
	}

	try {
		return new URL(modelUrl, globalThis.location?.href).host;
	} catch (_error) {
		return '';
	}
}

/**
 * Stable product identifier for Datadog (story AC "product ID"). Matches catalog + part on PDP.
 *
 * @param {string} [catalog]
 * @param {string} [skuStripped]
 * @returns {string}
 */
export function buildProductMedia3dProductId(catalog, skuStripped) {
	const hasCatalog = typeof catalog === 'string' && catalog !== '';
	const hasSku = typeof skuStripped === 'string' && skuStripped !== '';

	if (hasCatalog && hasSku) {
		return `${catalog}:${skuStripped}`;
	}

	if (hasSku) {
		return skuStripped;
	}

	if (hasCatalog) {
		return catalog;
	}

	return '';
}

/**
 * Media type is deliberately absent. Only the 3D viewer emits these logs, so it would be the same
 * value on every one of them.
 *
 * @param {{
 *   catalog?: string,
 *   modelUrl?: string,
 *   skuStripped?: string,
 * }} [metricDetail]
 * @returns {Record<string, string>}
 */
function buildProductMedia3dLogContext(metricDetail = {}) {
	/** @type {Record<string, string>} */
	const context = {};

	if (typeof metricDetail.catalog === 'string' && metricDetail.catalog !== '') {
		context.catalog = metricDetail.catalog;
	}

	if (typeof metricDetail.skuStripped === 'string' && metricDetail.skuStripped !== '') {
		context.sku_stripped = metricDetail.skuStripped;
	}

	const productId = buildProductMedia3dProductId(metricDetail.catalog, metricDetail.skuStripped);
	if (productId !== '') {
		context.product_id = productId;
	}

	return context;
}

/**
 * @param {'error'|'info'} level
 * @param {Record<string, string|number>} payload
 */
function logProductMedia3dViewerEvent(level, payload) {
	const logger = globalThis.rp_app?.logger;
	if (typeof logger?.[level] !== 'function') {
		return;
	}

	try {
		logger[level](payload);
	} catch (_error) {
	}
}

/**
 * Single Datadog emit for a 3D viewer outcome, parallel to the GA4 tracking on the same handlers.
 *
 * `outcome` only selects the success or failure log. The failure log then takes its outcome and
 * `error_type` from `errorReason`, so a caller's failure outcome is read as intent, never copied
 * through.
 *
 * @param {{
 *   errorReason?: string,
 *   loadDurationMs?: number,
 *   metricDetail?: {
 *     catalog?: string,
 *     modelUrl?: string,
 *     skuStripped?: string,
 *   },
 *   outcome: string,
 * }} viewerOutcome
 */
export function emitProductMedia3dViewerObservability({ errorReason, loadDurationMs, metricDetail, outcome }) {
	const payload = buildProductMedia3dLogContext(metricDetail);

	if (Number.isFinite(loadDurationMs)) {
		payload.load_duration_ms = Math.round(loadDurationMs);
	}

	if (outcome === PRODUCT_MEDIA_3D_VIEWER_OUTCOME.loaded) {
		logProductMedia3dViewerEvent('info', {
			...payload,
			message: PRODUCT_MEDIA_3D_VIEWER_LOADED_LOG_MESSAGE,
			metric: PRODUCT_MEDIA_3D_METRIC.viewerLoaded,
			outcome: PRODUCT_MEDIA_3D_VIEWER_OUTCOME.loaded,
		});

		return;
	}

	const classification = classifyProductMedia3dViewerFailure(errorReason);
	const modelUrlHost = readModelUrlHost(metricDetail?.modelUrl);

	if (modelUrlHost !== '') {
		payload.model_url_host = modelUrlHost;
	}

	logProductMedia3dViewerEvent('error', {
		...payload,
		error_reason: readSafeErrorReason(errorReason),
		error_type: classification.errorType,
		message: PRODUCT_MEDIA_3D_VIEWER_FAILURE_LOG_MESSAGE,
		metric: classification.metric,
		outcome: classification.outcome,
	});
}
